import { FrontendRoutes } from '@helpers/Constants/Navigation';
import { CreateNewOrderProps } from '@helpers/Models';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export const CreateOrderModal: React.FC<CreateNewOrderProps> = ({
  onClose,
  fromList,
}) => {
  const [isChosen, setIsChosen] = useState(false);
  const [isSelected, setIsSelected] = useState<'new' | 'existing'>();
  const navigate = useNavigate();

  /**
   * Handles navigation logic when confirming a customer action from a modal.
   *
   * Determines whether to navigate to the "Create Customer" or "Customer List" page
   * based on the user's selection (`isSelected`) and the context (`fromList`).
   *
   * Navigation only occurs if `fromList` is one of the supported values:
   * - "all_order"
   * - "service_processing"
   *
   * The navigation includes a state object with:
   * - `fromModal`: Indicates the action was triggered from a modal (always true)
   * - `fromList`: The current context list (e.g., "all_order", "service_processing")
   *
   */
  const onConfirm = () => {
    const isNew = isSelected === 'new';
    const lists = ['all_order', 'service_processing'];

    if (lists.includes(fromList)) {
      const targetRoute = isNew
        ? FrontendRoutes.CREATE_CUSTOMER
        : FrontendRoutes.CUSTOMER_LIST;

      navigate(targetRoute, {
        state: { fromModal: true, fromList },
      });
    }
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white w-full max-w-md rounded-lg shadow-lg p-4">
        {/* Modal Header */}
        <div className="flex justify-between items-center border-b pb-3">
          <h2 className="text-lg font-bold">Create New Order</h2>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900"
          >
            ✕
          </button>
        </div>
        <div className="borber-b">
          <button
            type="button"
            className={`text-left w-full p-3 mt-3 cursor-pointer hover:bg-tertiary hover:border hover:border-primary ${
              isSelected == 'new'
                ? 'bg-tertiary border border-primary'
                : 'bg-[#ECECEC]/35'
            }`}
            onClick={() => {
              setIsSelected('new');
              setIsChosen(true);
            }}
          >
            <h3 className="mb-2">New Customer</h3>
            <span className="text-[#5A5A5A]">
              Add new customer details to create a customer and place the order.
            </span>
          </button>

          <button
            type="button"
            className={`text-left w-full p-3 mt-3 cursor-pointer hover:bg-tertiary hover:border hover:border-primary ${
              isSelected == 'existing'
                ? 'bg-tertiary border border-primary'
                : 'bg-[#ECECEC]/35'
            }`}
            onClick={() => {
              setIsSelected('existing');
              setIsChosen(true);
            }}
          >
            <h3 className="mb-2">Existing Customer</h3>
            <span className="text-[#5A5A5A]">
              Select a customer from the existing customer list to place the
              order.
            </span>
          </button>
        </div>
        <div className="mt-6 flex justify-end gap-4">
          <button
            onClick={onConfirm}
            type="submit"
            disabled={!isChosen}
            className={`px-6 py-2 h-10 rounded-md text-white ${
              isChosen ? 'bg-primary' : 'bg-gray-400'
            }`}
          >
            Confirm
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-600 px-4 py-2"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};
